/**
 * How a caller names the document a redline route should work on, so the
 * document bytes never travel from the core to the web.
 *
 * The three places a document already lives:
 *
 * - `focusedDocument`: the document open in the Word add-in. The add-in
 *   uploads it once per chat turn into the `client_content_exchanges` table as a
 *   fulfilled `document` request whose payload carries the `.docx` bytes in
 *   base64. The core reads that same row; the web reads it here.
 * - `storedFile`: a conversation file in blob storage, addressed by its
 *   canonical storage URL (`s3://bucket/key` or `azure://container/key`).
 * - `ooxml`: markup the caller already holds, such as the selection the add-in
 *   sends with a `redline_selection` request. Any of the shapes `OoxmlRange`
 *   accepts: a `pkg:package`, a `w:document`, or bare paragraphs.
 */
import { z } from "zod";

export const focusedDocumentReferenceSchema = z.object({
    kind: z.literal("focusedDocument"),
    conversationId: z.string().min(1),
    parentConversationId: z.string().min(1).nullable(),
});

export const storedFileReferenceSchema = z.object({
    kind: z.literal("storedFile"),
    fileUri: z.string().min(1),
});

export const ooxmlReferenceSchema = z.object({
    kind: z.literal("ooxml"),
    documentFlatOpc: z.string().min(1),
});

export const documentReferenceSchema = z.discriminatedUnion("kind", [focusedDocumentReferenceSchema, storedFileReferenceSchema, ooxmlReferenceSchema]);

export type FocusedDocumentReference = z.infer<typeof focusedDocumentReferenceSchema>;
export type StoredFileReference = z.infer<typeof storedFileReferenceSchema>;
export type OoxmlReference = z.infer<typeof ooxmlReferenceSchema>;
export type DocumentReference = z.infer<typeof documentReferenceSchema>;
